import type {
  AppApi,
  AppRole,
  AdminUgcApi,
  AdminImageGenApi,
  AdminVideoGenApi,
  AdminSpriteGenApi,
  AdminAudioGenApi,
  AdminThreeDGenApi,
  ImageGenEntry,
  ImageGenReport,
  VideoGenEntry,
  VideoGenReport,
  SpriteGenEntry,
  SpriteGenReport,
  AudioGenEntry,
  AudioGenReport,
  ThreeDGenEntry,
  ThreeDGenReport,
  ReleaseNote,
} from './AppApi'
import type { LaunchIntent, ResolveLaunchIntentOptions } from './LaunchIntent'
import { HttpAppApi } from './HttpAppApi'
import { MockAdminUgcApi } from './MockAdminUgcApi'
import { createMockAdminGenApi } from './adminGen'
import { MOCK_RELEASE_NOTES } from './MockAppApi'
import { warnRenamed } from '../deprecationWarning'

const LOCAL_STORAGE_KEY = 'rundot-game-sandbox-app-role'

const ROLE_RANK: Record<AppRole, number> = { owner: 2, editor: 1, none: 0 }

function isDowngrade(realRole: AppRole, overrideRole: AppRole): boolean {
  return ROLE_RANK[overrideRole] < ROLE_RANK[realRole]
}

function getStoredRole(): AppRole | null {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (stored === 'owner' || stored === 'editor' || stored === 'none') {
      return stored
    }
  } catch {
    // localStorage unavailable
  }
  return null
}

export function setStoredAppRole(role: AppRole): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, role)
  } catch {
    // localStorage unavailable
  }
}

/**
 * Playground-aware AppApi that overrides getMyRole() with a configured role.
 *
 * When HTTP is available (dev/staging/playground), the real role is fetched
 * first. The override is only applied if it's a *downgrade* from the real
 * role — an owner can test as editor/player, but a player cannot escalate.
 *
 * In local emulator mode (no auth), the override is applied directly since
 * the real role cannot be fetched.
 */
export class PlaygroundAppApi implements AppApi {
  readonly adminUgc: AdminUgcApi
  readonly adminImageGen: AdminImageGenApi
  readonly adminVideoGen: AdminVideoGenApi
  readonly adminSpriteGen: AdminSpriteGenApi
  readonly adminAudioGen: AdminAudioGenApi
  readonly adminThreeDGen: AdminThreeDGenApi
  private readonly configRole: AppRole | undefined
  private readonly httpApp: HttpAppApi | null

  constructor(appId: string, configRole?: AppRole, useHttp: boolean = true) {
    this.configRole = configRole

    if (useHttp) {
      this.httpApp = new HttpAppApi(appId)
      this.adminUgc = this.httpApp.adminUgc
      this.adminImageGen = this.httpApp.adminImageGen
      this.adminVideoGen = this.httpApp.adminVideoGen
      this.adminSpriteGen = this.httpApp.adminSpriteGen
      this.adminAudioGen = this.httpApp.adminAudioGen
      this.adminThreeDGen = this.httpApp.adminThreeDGen
    } else {
      this.httpApp = null
      this.adminUgc = new MockAdminUgcApi()
      this.adminImageGen = createMockAdminGenApi<ImageGenEntry, ImageGenReport>('adminImageGen')
      this.adminVideoGen = createMockAdminGenApi<VideoGenEntry, VideoGenReport>('adminVideoGen')
      this.adminSpriteGen = createMockAdminGenApi<SpriteGenEntry, SpriteGenReport>('adminSpriteGen')
      this.adminAudioGen = createMockAdminGenApi<AudioGenEntry, AudioGenReport>('adminAudioGen')
      this.adminThreeDGen = createMockAdminGenApi<ThreeDGenEntry, ThreeDGenReport>('adminThreeDGen')
    }
  }

  async getMyRole(): Promise<AppRole> {
    const overrideRole = getStoredRole() ?? this.configRole ?? null

    if (this.httpApp) {
      const realRole = await this.httpApp.getMyRole()
      if (overrideRole && isDowngrade(realRole, overrideRole)) {
        console.warn(
          `[RUN:playground] app.getMyRole() downgrading '${realRole}' → '${overrideRole}' (playground override)`,
        )
        return overrideRole
      }
      if (overrideRole && overrideRole !== realRole) {
        console.warn(
          `[RUN:playground] app.getMyRole() ignoring override '${overrideRole}' — cannot upgrade from '${realRole}'`,
        )
      }
      return realRole
    }

    // Local mode: no HTTP, apply override directly (emulator is trusted)
    if (overrideRole) {
      console.warn(
        `[RUN:playground] app.getMyRole() returning playground role: '${overrideRole}' (local mode)`,
      )
      return overrideRole
    }
    return 'none'
  }

  async resolveLaunchIntent(
    _options?: ResolveLaunchIntentOptions,
  ): Promise<LaunchIntent> {
    return { kind: 'none', params: {} }
  }

  async getReleaseNotesAsync(): Promise<ReleaseNote[]> {
    // Same convention as getMyRole(): in remote preview (dev/staging/playground)
    // fetch the game's real published notes over HTTP; fall back to sample data
    // only in the local emulator where there's no auth/backend.
    if (this.httpApp) {
      return this.httpApp.getReleaseNotesAsync()
    }
    return MOCK_RELEASE_NOTES
  }

  async openReleaseNotesAsync(): Promise<void> {
    // No host info page in the playground (matches HttpAppApi) — preview has no
    // GameInfoPanel to surface. The call resolves so games can stay host-agnostic.
  }
}

/** @deprecated Use `PlaygroundAppApi`. */
export class SandboxAppApi extends PlaygroundAppApi { // @deprecated-alias
  constructor(...args: ConstructorParameters<typeof PlaygroundAppApi>) {
    warnRenamed('SandboxAppApi', 'PlaygroundAppApi') // @deprecated-alias
    super(...args)
  }
}
